import React, { memo, useCallback, useMemo } from 'react';

import { Box, Popover, Tooltip, Typography } from '@mui/material';
import { ConfirmIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { resolveColor, SYSTEM_COLORS } from 'constants/colors';
import usePopover from 'hooks/usePopover';

import FieldWrapper from '../FieldWrapper';
import Label from '../Label';

import {
  getSwatchStyles,
  getTriggerSwatchStyles,
  gridContainerStyles,
  popoverPaperStyles,
  triggerBoxStyles,
} from './ColorPickerCDS.styles';

const anchorOriginConfig = { vertical: 'bottom', horizontal: 'left' };
const transformOriginConfig = { vertical: 'top', horizontal: 'left' };
const paperSlotProps = { paper: { sx: popoverPaperStyles } };

const ColorPickerCDS = ({
  name = '',
  label = '',
  value = '',
  onChange,
  disabled = false,
  error = false,
  helperText = '',
  required = false,
  colors = null,
  className = '',
  width = '100%',
  columns = 1,
}) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover(true);

  const resolved = useMemo(() => resolveColor(value), [value]);

  const onOpen = useCallback(
    e => {
      if (disabled) {
        return;
      }

      onSetAnchorElement(e);
    },
    [disabled, onSetAnchorElement],
  );

  const onSelectColor = useCallback(
    e => {
      const { colorname } = e.currentTarget.dataset;

      onChange({ target: { name, value: colorname } });
      onClearAnchorElement(e);
    },
    [name, onChange, onClearAnchorElement],
  );

  const isOpen = !_.isNil(anchorEl);
  const displayName = resolved?.name || '';
  const displayBg = resolved?.bg || '';

  return (
    <FieldWrapper className={className} columns={columns} width={width}>
      <Box>
        {!!label && (
          <Label error={error} name={name} required={required}>
            {label}
          </Label>
        )}

        <Box
          data-name={name}
          role="button"
          sx={triggerBoxStyles}
          tabIndex={disabled ? -1 : 0}
          onClick={onOpen}
        >
          <Box sx={getTriggerSwatchStyles(displayBg)} />
          <Typography color={displayName ? 'text.primary' : 'text.secondary'} variant="regular14">
            {displayName || 'Select a color'}
          </Typography>
        </Box>

        {!!helperText && (
          <Typography color="error" variant="regular12">
            {helperText}
          </Typography>
        )}

        <Popover
          anchorEl={anchorEl}
          anchorOrigin={anchorOriginConfig}
          open={isOpen}
          slotProps={paperSlotProps}
          transformOrigin={transformOriginConfig}
          onClose={onClearAnchorElement}
        >
          <Box sx={gridContainerStyles}>
            {(colors || SYSTEM_COLORS).map(color => {
              const isSelected = resolved?.name === color.name;

              return (
                <Tooltip key={color.name} placement="top" title={color.name}>
                  <Box
                    component="button"
                    data-colorname={color.name}
                    sx={getSwatchStyles({ bg: color.bg, isSelected })}
                    type="button"
                    onClick={onSelectColor}
                  >
                    {isSelected && <ConfirmIcon size={16} />}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Popover>
      </Box>
    </FieldWrapper>
  );
};

ColorPickerCDS.propTypes = {
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      bg: PropTypes.string.isRequired,
      fg: PropTypes.string.isRequired,
      dot: PropTypes.string.isRequired,
    }),
  ),
  columns: PropTypes.number,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  width: PropTypes.string,
};

export default memo(ColorPickerCDS);
